export const INTRO_CALL_URL =
  "https://cal.com/guidione-machava-1gryqg/client-intro-call";
export const CONTACT_EMAIL = "guidione@world-class.design";
export const SHORTLIST_PRICE = "€3,000";
export const FULL_HIRE_PERCENTAGE = "10%";
export const FULL_HIRE_PRICE = `${SHORTLIST_PRICE} + ${FULL_HIRE_PERCENTAGE}`;
export const FULL_HIRE_PRICE_NOTE = "of first-year salary";

// Facts the redesign still needs confirmed. While a value is null, the copy that
// depends on it falls back to wording without the missing detail.
/** Working days from the brief call to the first names on the shortlist. */
export const FIRST_NAMES_WORKING_DAYS: number | null = null;
/** When the Full hire percentage is invoiced, e.g. "when the designer signs". */
export const FULL_HIRE_BALANCE_DUE: string | null = null;
/** One line on what each score level (0 to 3) means. */
export const SCORE_SCALE_NOTE: string | null = null;
/** What agencies get when they work with us. */
export const AGENCY_OFFER: string | null = null;

interface SearchStep {
  title: string;
  /** Timeline version: one-line title and a two-line summary of similar length. */
  shortTitle: string;
  summary: string;
  duration: string;
  weDo: string;
  youGet: string;
}

export const SHORTLIST_STEP_COUNT = 3;

export const searchSteps: SearchStep[] = [
  {
    title: "Brief and hiring criteria",
    shortTitle: "Brief and criteria",
    summary: "Criteria you agree on, used to judge every name.",
    duration: "30-minute call",
    weDo: "On a 30-minute brief call, we define the role, the level and the hiring criteria with you.",
    youGet:
      "Hiring criteria you agreed on. Every candidate is judged against them.",
  },
  {
    title: "Search and portfolio review",
    shortTitle: "Portfolio search",
    summary: "Portfolios reviewed by hand, with updates as we go.",
    duration: FIRST_NAMES_WORKING_DAYS
      ? `${FIRST_NAMES_WORKING_DAYS} working days`
      : "Days, not weeks",
    weDo: "We search our network and review portfolios by hand, checking what each designer actually did on products that shipped.",
    youGet: "Updates as we go. No silent weeks.",
  },
  {
    title: "Ranked shortlist",
    shortTitle: "Ranked shortlist",
    summary: "Ranked names, with the reasons behind each one.",
    duration: FIRST_NAMES_WORKING_DAYS
      ? `First names by day ${FIRST_NAMES_WORKING_DAYS}`
      : "First names in days",
    weDo: "We rank candidates against your criteria and write a one-page review for each of them.",
    youGet: "A ranked shortlist with the reasons behind every name.",
  },
  {
    title: "Screening and interviews",
    shortTitle: "Screening and interviews",
    summary: "Interviews in your calendar, status in one place.",
    duration: "At your interview pace",
    weDo: "Warm introductions, screening calls and interview scheduling on your booking link. We keep your tracking board and a shared Slack channel up to date.",
    youGet:
      "Interviews in your calendar and the status of every candidate in one place.",
  },
  {
    title: "Decision and start date",
    shortTitle: "Decision and start",
    summary: "We follow up until your new designer starts.",
    duration: "Until the designer starts",
    weDo: "We follow up with candidates and keep the process moving to the start date.",
    youGet: "One contact from offer to start date, so nothing stalls.",
  },
];

/** Fictional candidates used by the hero animation. */
interface ShortlistCandidate {
  name: string;
  title: string;
  /** Generated portrait (fictional person) in /public/home/candidates. */
  photo: string;
  /** Match against the brief, in percent. */
  match: number;
  why: string;
}

/** Size of the example shortlist the hero delivers to the inbox. */
export const HERO_SHORTLIST_SIZE = 15;

export const heroShortlist: ShortlistCandidate[] = [
  {
    name: "Lena Hoffmann",
    photo: "/home/candidates/lena-hoffmann.webp",
    title: "Senior Product Designer, Berlin",
    match: 94,
    why: "Shipped a full checkout redesign and built the design system behind it.",
  },
  {
    name: "Marta Kowalska",
    photo: "/home/candidates/marta-kowalska.webp",
    title: "Product Designer, Warsaw",
    match: 88,
    why: "Shows real user testing and how it changed the product.",
  },
  {
    name: "Tomás Ferreira",
    photo: "/home/candidates/tomas-ferreira.webp",
    title: "Senior UX Designer, Lisbon",
    match: 85,
    why: "Clear, well structured flows on a complex payments product.",
  },
];

interface ReviewScore {
  label: string;
  score: number;
  note: string;
}

export const sampleReview = {
  initials: "TF",
  name: "Tomás Ferreira",
  summary: "Senior UX Designer, 6 years, Lisbon, open to remote",
  rank: 3,
  verdict: "Worth interviewing",
  overall: 2.2,
  level: "Senior",
  project: "payments dashboard redesign",
  scores: [
    {
      label: "Craft and visual design",
      score: 2.4,
      note: "Polished, consistent screens. A few small spacing mistakes on secondary pages.",
    },
    {
      label: "User research",
      score: 1.8,
      note: "Says research was done but shows little proof. No user testing or user quotes.",
    },
    {
      label: "Interaction and systems thinking",
      score: 2.3,
      note: "Strong user flows. Reuses parts of the interface but doesn't show a clear design system.",
    },
    {
      label: "Communication and storytelling",
      score: 2.1,
      note: "Explains the problem well. Says the project had impact but gives no numbers.",
    },
  ] satisfies ReviewScore[],
};

export interface BoardCandidate {
  initials: string;
  name: string;
  photo: string;
  role: string;
  status: string;
}

export interface BoardColumn {
  title: string;
  /** Column status dot. */
  dot: string;
  candidates: BoardCandidate[];
}

export const trackingBoard: BoardColumn[] = [
  {
    title: "Shortlisted",
    dot: "#B9AFA8",
    candidates: [
      {
        initials: "AR",
        name: "Ana Ruiz",
        photo: "/home/candidates/ana-ruiz.webp",
        role: "Product Designer, Barcelona",
        status: "Waiting for your feedback",
      },
      {
        initials: "JB",
        name: "Jonas Berg",
        photo: "/home/candidates/jonas-berg.webp",
        role: "Senior Product Designer, Stockholm",
        status: "Review sent to you",
      },
      {
        initials: "SM",
        name: "Sofia Marques",
        photo: "/home/candidates/sofia-marques.webp",
        role: "Product Designer, Porto",
        status: "Review sent to you",
      },
      {
        initials: "LN",
        name: "Lukas Novak",
        photo: "/home/candidates/lukas-novak.webp",
        role: "UX Designer, Prague",
        status: "Waiting for your feedback",
      },
    ],
  },
  {
    title: "Screening call",
    dot: "#F97316",
    candidates: [
      {
        initials: "TF",
        name: "Tomás Ferreira",
        photo: "/home/candidates/tomas-ferreira.webp",
        role: "Senior UX Designer, Lisbon",
        status: "Screening call booked by WCD",
      },
      {
        initials: "CM",
        name: "Chloé Martin",
        photo: "/home/candidates/chloe-martin.webp",
        role: "Product Designer, Paris",
        status: "Passed screening, notes shared",
      },
      {
        initials: "AO",
        name: "Amara Okafor",
        photo: "/home/candidates/amara-okafor.webp",
        role: "Senior Product Designer, Amsterdam",
        status: "Screening call booked by WCD",
      },
    ],
  },
  {
    title: "Your interview",
    dot: "#2B1B1C",
    candidates: [
      {
        initials: "MK",
        name: "Marta Kowalska",
        photo: "/home/candidates/marta-kowalska.webp",
        role: "Product Designer, Warsaw",
        status: "Interview booked in your calendar",
      },
      {
        initials: "ID",
        name: "Inès Dubois",
        photo: "/home/candidates/ines-dubois.webp",
        role: "Senior Product Designer, Lyon",
        status: "Interview booked in your calendar",
      },
      {
        initials: "MG",
        name: "Mateo García",
        photo: "/home/candidates/mateo-garcia.webp",
        role: "Product Designer, Madrid",
        status: "Second interview this week",
      },
    ],
  },
  {
    title: "Your decision",
    dot: "#6DA83E",
    candidates: [
      {
        initials: "LH",
        name: "Lena Hoffmann",
        photo: "/home/candidates/lena-hoffmann.webp",
        role: "Senior Product Designer, Berlin",
        status: "Interviews done, your call",
      },
      {
        initials: "EV",
        name: "Elias Virtanen",
        photo: "/home/candidates/elias-virtanen.webp",
        role: "Product Designer, Helsinki",
        status: "Waiting for your decision",
      },
    ],
  },
];
